import { WidgetRequestTypeLabels } from '@televet/shared-types/widgetRequest/WidgetRequestTypeLabels';
import { useEffect, useState } from 'react';
import useClinicUser from 'shared/hooks/useClinicUser';
import { TaskType } from 'shared/types/graphql';
import { TaskList } from '../types';
import useTaskListOptions from './useTaskListOptions';

type UseGenerateTaskListsReturnType = {
  loaded: boolean;
  taskLists: TaskList[];
};

const colorList = [
  'var(--chakra-colors-text-brand)',
  '#CE372D',
  '#E39435',
  '#E6C543',
  '#76BE9B',
  '#5DABC0',
  '#98A2F3',
  '#87888F',
  '#484848',
];

const useGenerateTaskLists = (): UseGenerateTaskListsReturnType => {
  const { clinicUserId, currentClinicId } = useClinicUser();
  const {
    clinicAppointmentTypes,
    otherTypes,
    loaded: taskListOptionsLoaded,
  } = useTaskListOptions(currentClinicId || '');
  const [loaded, setLoaded] = useState(false);
  const [taskLists, setTaskLists] = useState<TaskList[]>([]);

  useEffect(() => {
    if (taskListOptionsLoaded && !loaded && currentClinicId && clinicAppointmentTypes) {
      let colorIndex = 0;

      const _taskLists: TaskList[] = (otherTypes || []).map(({ taskType, displayName }) => {
        const list = {
          id: taskType,
          name: displayName,
          color: colorList[colorIndex % colorList.length],
          taskFilters: [
            {
              id: taskType,
              taskType,
            },
          ],
        };
        colorIndex++;
        return list;
      });

      const filteredAppointmentTypes = clinicAppointmentTypes.filter(
        ({ isActive, isDeleted, hasPendingTasks, shouldCreateTask }) =>
          hasPendingTasks || (isActive && !isDeleted && shouldCreateTask),
      );

      for (const widgetRequest of filteredAppointmentTypes || []) {
        const { id, displayName, requestType, isActive, isDeleted, __typename } = widgetRequest;
        const name =
          (displayName || WidgetRequestTypeLabels[requestType] || requestType) +
          (isDeleted ? ' (Deleted)' : isActive ? '' : ' (Inactive)');

        const taskList: TaskList = {
          id,
          name,
          color: colorList[colorIndex % colorList.length],
          taskFilters: [
            {
              id,
              taskType: TaskType.WidgetRequest,
              metadata: {
                entities: [
                  {
                    entityName: __typename || '',
                    entityId: id,
                  },
                ],
              },
            },
          ],
          isInactiveOrDeleted: !!(!isActive || isDeleted),
        };
        colorIndex++;
        _taskLists.push(taskList);
      }

      setTaskLists(_taskLists);
      setLoaded(true);
    }
  }, [clinicAppointmentTypes, clinicUserId, currentClinicId, loaded, otherTypes, taskListOptionsLoaded]);

  return { loaded, taskLists };
};

export default useGenerateTaskLists;
